import { useEffect, useRef, useState } from "react";
import "@/components/TextPromptModal.css";

/**
 * Followups #13 — Field-creation modal with explicit type picker.
 *
 * Replaces the TextPromptModal use site for batch-field creation, which
 * previously hardcoded dataType to 'number'. Same visual chrome as
 * TextPromptModal (reuses its CSS) plus a `<select>` for data type.
 *
 * dataType options match the form-config FieldContent['dataType'] union.
 */
const DATA_TYPES = [
  { value: "text", label: "Text" },
  { value: "number", label: "Number" },
  { value: "decimal", label: "Decimal" },
  { value: "date", label: "Date" },
  { value: "boolean", label: "Boolean" },
] as const;

export type FieldDataType = typeof DATA_TYPES[number]["value"];

interface FieldCreateModalProps {
  open: boolean;
  title: string;
  description?: string;
  placeholder?: string;
  defaultName?: string;
  defaultDataType?: FieldDataType;
  submitLabel?: string;
  cancelLabel?: string;
  /** Validate the field name. Return a string error to block submit. */
  validate?: (name: string) => string | null;
  onConfirm: (name: string, dataType: FieldDataType) => void;
  onCancel: () => void;
}

export function FieldCreateModal({
  open,
  title,
  description,
  placeholder,
  defaultName = "",
  defaultDataType = "text",
  submitLabel = "Add field",
  cancelLabel = "Cancel",
  validate,
  onConfirm,
  onCancel,
}: FieldCreateModalProps) {
  const [name, setName] = useState(defaultName);
  const [dataType, setDataType] = useState<FieldDataType>(defaultDataType);
  const [error, setError] = useState<string | null>(null);
  const nameRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (open) {
      setName(defaultName);
      setDataType(defaultDataType);
      setError(null);
      requestAnimationFrame(() => {
        nameRef.current?.focus();
        nameRef.current?.select();
      });
    }
  }, [open, defaultName, defaultDataType]);

  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") onCancel();
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onCancel]);

  if (!open) return null;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = name.trim();
    if (!trimmed) {
      setError("Please enter a name");
      return;
    }
    if (validate) {
      const v = validate(trimmed);
      if (v) {
        setError(v);
        return;
      }
    }
    onConfirm(trimmed, dataType);
  };

  return (
    <div className="text-prompt-overlay" onClick={onCancel}>
      <div
        className="text-prompt-panel"
        role="dialog"
        aria-labelledby="field-create-title"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 id="field-create-title" className="text-prompt-title">{title}</h2>
        {description && <p className="text-prompt-description">{description}</p>}
        <form onSubmit={handleSubmit}>
          <input
            ref={nameRef}
            className="text-prompt-input"
            value={name}
            placeholder={placeholder}
            onChange={(e) => { setName(e.target.value); setError(null); }}
          />
          <label className="text-prompt-input" style={{ display: "flex", alignItems: "center", gap: "8px", paddingTop: 0, paddingBottom: 0 }}>
            <span style={{ flex: "0 0 auto", color: "var(--color-text-secondary)" }}>Type</span>
            <select
              value={dataType}
              onChange={(e) => setDataType(e.target.value as FieldDataType)}
              style={{ flex: 1, background: "transparent", border: "none", color: "var(--color-text-primary)", outline: "none" }}
              data-testid="field-create-type"
            >
              {DATA_TYPES.map((t) => (
                <option key={t.value} value={t.value}>{t.label}</option>
              ))}
            </select>
          </label>
          {error && <div className="text-prompt-error">{error}</div>}
          <div className="text-prompt-actions">
            <button type="button" className="text-prompt-btn" onClick={onCancel}>
              {cancelLabel}
            </button>
            <button type="submit" className="text-prompt-btn text-prompt-btn--primary">
              {submitLabel}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
